import * as React from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import EditIcon from '@material-ui/icons/Edit';
import { ContactTypesType } from '../api/contactType';
import { UserType } from '../api/users';
import formatDate from '../utils/formatDate';

interface Props {
  onEditStep: (step: number) => void;
  savedContacts: UserType[] | null;
  contactTypes: ContactTypesType[] | null;
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  '1_2_FAMILY': '1 & 2 Family Dwelling',
  MULTIFAMILY: 'Multifamily',
  COMMERCIAL: 'Commercial',
};

const formatContactName = (contact: any) => {
  if (!contact) return '';
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  const detail = contact.email
    || contact.companyName
    || contact.parentCompany?.companyName
    || contact.parentCompany;
  return [name || contact.companyName, detail].filter(Boolean).join(' - ');
};

const formatExpiration = (date: any) => {
  if (!date) return '—';
  try {
    if (date instanceof Date) return formatDate(date);
    return String(date);
  } catch (e) {
    return '—';
  }
};

const ProjectFormReview = ({ onEditStep, savedContacts, contactTypes }: Props) => {
  const { watch } = useFormContext();
  const data = watch();

  const projectContacts: any[] = Array.isArray(data.projectContacts) ? data.projectContacts : [];
  const permitContacts: any[] = savedContacts && savedContacts.length
    ? savedContacts
    : (Array.isArray(data.contacts) ? data.contacts : []);

  const projectManager = data.manager
    ? [data.manager.firstName, data.manager.lastName].filter(Boolean).join(' ')
    : '';
  const permitManager = data.permitManager
    ? [data.permitManager.firstName, data.permitManager.lastName].filter(Boolean).join(' ')
    : '';
  const serviceTypes = Array.isArray(data.serviceType)
    ? data.serviceType.map((s: any) => s.title || s.name).filter(Boolean).join(', ')
    : '';
  const projectType = PROJECT_TYPE_LABELS[data.project_type] || data.project_type || '—';

  return (
    <ReviewWrap>
      <Lead>Review your entries before submitting. Use the Edit button on any section to make changes.</Lead>

      <Section>
        <SectionHeader>
          <SectionTitle>Address</SectionTitle>
          <EditButton type="button" onClick={() => onEditStep(0)}>
            <EditIcon style={{ fontSize: 16 }} />
            Edit
          </EditButton>
        </SectionHeader>
        <Grid>
          <Field>
            <Label>Address</Label>
            <Value>{data.address || '—'}</Value>
          </Field>
          <Field>
            <Label>Street</Label>
            <Value>{data.street || '—'}</Value>
          </Field>
          <Field>
            <Label>City</Label>
            <Value>{data.city || '—'}</Value>
          </Field>
          <Field>
            <Label>State</Label>
            <Value>{data.state || '—'}</Value>
          </Field>
          <Field>
            <Label>Zip</Label>
            <Value>{data.zipCode || '—'}</Value>
          </Field>
        </Grid>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Project Details</SectionTitle>
          <EditButton type="button" onClick={() => onEditStep(1)}>
            <EditIcon style={{ fontSize: 16 }} />
            Edit
          </EditButton>
        </SectionHeader>
        <Grid>
          <Field>
            <Label>Project Name</Label>
            <Value>{data.name || '—'}</Value>
          </Field>
          <Field>
            <Label>Jurisdiction</Label>
            <Value>{data.jurisdiction?.name || '—'}</Value>
          </Field>
          <Field>
            <Label>Project Manager</Label>
            <Value>{projectManager || '—'}</Value>
          </Field>
          <Field>
            <Label>Project Type</Label>
            <Value>{projectType}</Value>
          </Field>
        </Grid>
        <SubLabel>Contacts</SubLabel>
        {projectContacts.length === 0 ? (
          <EmptyText>No contacts selected</EmptyText>
        ) : (
          <ChipList>
            {projectContacts.map((c: any) => (
              <ContactChip key={c.id}>{formatContactName(c) || 'Unknown'}</ContactChip>
            ))}
          </ChipList>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Permit</SectionTitle>
          <EditButton type="button" onClick={() => onEditStep(2)}>
            <EditIcon style={{ fontSize: 16 }} />
            Edit
          </EditButton>
        </SectionHeader>
        <Grid>
          <Field>
            <Label>Permit Name</Label>
            <Value>{data.permitName || '—'}</Value>
          </Field>
          <Field>
            <Label>Service Type</Label>
            <Value>{serviceTypes || '—'}</Value>
          </Field>
          <Field>
            <Label>Work Scope</Label>
            <Value>{data.workScope?.title || '—'}</Value>
          </Field>
          <Field>
            <Label>Project Manager</Label>
            <Value>{permitManager || '—'}</Value>
          </Field>
          <Field>
            <Label>Count of Inspections</Label>
            <Value>{data.inspectionsNumber || '—'}</Value>
          </Field>
          <Field>
            <Label>Expiration Date</Label>
            <Value>{formatExpiration(data.expirationDate)}</Value>
          </Field>
        </Grid>
        <FullField>
          <Label>Description</Label>
          <Value>{data.description || '—'}</Value>
        </FullField>
        <SubLabel>Contacts</SubLabel>
        {permitContacts.length === 0 ? (
          <EmptyText>No contacts selected</EmptyText>
        ) : (
          <ContactsTableWrap>
            <ContactsTableEl>
              <thead>
                <tr>
                  <th>Contact Name</th>
                  <th>Contact Type</th>
                </tr>
              </thead>
              <tbody>
                {permitContacts.map((c: any) => {
                  const ctId = c.contactType ?? c.contact_type;
                  const type = (contactTypes || []).find(ct => ct.id === Number(ctId));
                  return (
                    <tr key={c.id}>
                      <td>{formatContactName(c) || 'Unknown'}</td>
                      <td>{type?.name || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </ContactsTableEl>
          </ContactsTableWrap>
        )}
      </Section>
    </ReviewWrap>
  );
};

const ReviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
`;

const Lead = styled.p`
  font-size: var(--fs-sm);
  color: var(--neutral-600);
  margin: 0 0 var(--space-2);
`;

const Section = styled.section`
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  padding: var(--space-5);
`;

const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--neutral-100);
`;

const SectionTitle = styled.h3`
  font-size: 1.0625rem;
  font-weight: var(--fw-semibold);
  color: var(--neutral-800);
  margin: 0;
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: var(--neutral-0);
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  color: var(--primary-600);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard),
    border-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-4) var(--space-5);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const FullField = styled(Field)`
  margin-top: var(--space-4);
  grid-column: 1 / -1;
`;

const Label = styled.span`
  font-size: 0.6875rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const Value = styled.span`
  font-size: var(--fs-base);
  color: var(--neutral-800);
  word-break: break-word;
  white-space: pre-wrap;
`;

const SubLabel = styled.div`
  margin-top: var(--space-4);
  margin-bottom: var(--space-2);
  font-size: 0.6875rem;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const EmptyText = styled.div`
  font-size: var(--fs-sm);
  color: var(--neutral-400);
  padding: var(--space-2) 0;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const ContactChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: var(--primary-50);
  border: 1px solid var(--primary-200);
  border-radius: 999px;
  font-size: var(--fs-sm);
  color: var(--primary-800);
`;

const ContactsTableWrap = styled.div`
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  overflow: hidden;
`;

const ContactsTableEl = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: var(--neutral-50);
  }

  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 0.6875rem;
    font-weight: var(--fw-semibold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--neutral-500);
    border-bottom: 1px solid var(--neutral-200);
  }

  td {
    padding: 10px 14px;
    font-size: var(--fs-sm);
    color: var(--neutral-800);
    border-bottom: 1px solid var(--neutral-100);
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

export default ProjectFormReview;
